import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getGoalProgress, getNextSteps, startScoreImprovementPlan } from 'thunks';
import { AICoachGoalPriority, AICoachGoalType } from 'enums/AICoach';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';

const useAutoStartScoreImprovementPlan = () => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application } = useSelector(getApplicationData);
  const { aiCoachGoals, isStartingScoreImprovementPlan } = useSelector(getLoanGoal);
  const hasStartedRef = useRef(false);

  const hasSecondaryImproveScoreGoal = aiCoachGoals?.some(
    (goal) => goal.type === AICoachGoalType.ImproveCreditScore && goal.priority !== AICoachGoalPriority.Primary,
  );

  useEffect(() => {
    if (!application?.id || !hasSecondaryImproveScoreGoal || isStartingScoreImprovementPlan || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    const startPlan = async () => {
      try {
        await dispatchWithUnwrap(startScoreImprovementPlan(application.id));
        if (application.borrowerId) {
          dispatch(getGoalProgress(application.borrowerId));
          dispatch(getNextSteps(application.borrowerId));
        }
      } catch (error) {
        hasStartedRef.current = false;
        analytics.track('Failed to start score improvement plan', { error });
      }
    };

    startPlan();
  }, [
    application?.id,
    application?.borrowerId,
    hasSecondaryImproveScoreGoal,
    isStartingScoreImprovementPlan,
    dispatch,
    dispatchWithUnwrap,
  ]);
};

export default useAutoStartScoreImprovementPlan;
